/**
 * Organisation policy — RX-ORG-09.
 *
 * Decisions a care home makes once, for everyone it looks after, that an
 * individual staff member must not be able to change on a whim: how hard the
 * escalation ladder pushes, whether nurses see medical history, whether
 * families can be invited onto a resident's record.
 *
 * Stored as an untyped JSON blob on `organizations.settings`, which is the
 * pragmatic shape for something that will grow — and exactly why parsing it
 * needs care. The blob is written by an admin through an API, survives schema
 * changes, and may hold keys from a version of the product that no longer
 * exists. So `parseOrgPolicy` never trusts it: every field is validated
 * individually and a bad one falls back to the default rather than poisoning
 * the whole policy.
 *
 * That matters more than it sounds. The alternative -- reject the blob if any
 * field is wrong -- means one stale key from an old release silently reverts a
 * care home's escalation timings to the defaults, and nobody finds out until
 * a resident's missed dose is reported thirty minutes later than the home
 * believes it will be.
 */
import type { EscalationPolicy } from './escalation.js';
import { DEFAULT_ESCALATION_POLICY } from './escalation.js';

export interface OrgPolicy {
  /**
   * Minutes after a dose is due before caregivers hear about it.
   *
   * A hospital ward wants this short. A home-care agency covering a wide area
   * wants it longer, because a nurse twenty minutes away being told at minute
   * five is noise that trains people to ignore the alert.
   */
  lateAfterMinutes: number;
  /** The same, for a medication marked critical. Always the tighter of the two. */
  criticalLateAfterMinutes: number;

  /**
   * Whether the `staff` role may read medical history for residents this
   * organisation OWNS.
   *
   * Default false, matching the role's own capabilities: a nurse needs to know
   * what is due now, not a year of adherence. A home that decides otherwise
   * can say so -- but only for records it is the controller of. A patient the
   * home merely has access to belongs to their family, and no setting on the
   * home's row may widen what the family granted.
   */
  staffCanSeeHistory: boolean;

  /**
   * Whether family members can be invited onto a resident's record.
   *
   * Default true. Homes that route all family contact through their own staff
   * turn it off; the point of the setting is that a single staff member cannot
   * make that call one resident at a time.
   */
  allowFamilyInvites: boolean;
}

export const DEFAULT_ORG_POLICY: OrgPolicy = {
  lateAfterMinutes: DEFAULT_ESCALATION_POLICY.lateAfterMinutes,
  criticalLateAfterMinutes: DEFAULT_ESCALATION_POLICY.criticalLateAfterMinutes,
  staffCanSeeHistory: false,
  allowFamilyInvites: true,
};

/** Bounds on the escalation timings, in minutes. */
export const MIN_LATE_AFTER = 1;
export const MAX_LATE_AFTER = 720;

function clampMinutes(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const rounded = Math.round(value);
  if (rounded < MIN_LATE_AFTER || rounded > MAX_LATE_AFTER) return fallback;
  return rounded;
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Read a stored blob into a policy, field by field.
 *
 * Unknown keys are ignored, malformed values fall back individually, and the
 * result is always usable. `null` and `undefined` are the common case -- most
 * organisations never set anything.
 */
export function parseOrgPolicy(settings: unknown): OrgPolicy {
  if (settings === null || typeof settings !== 'object' || Array.isArray(settings)) {
    return { ...DEFAULT_ORG_POLICY };
  }
  const raw = settings as Record<string, unknown>;

  const lateAfterMinutes = clampMinutes(
    raw.lateAfterMinutes,
    DEFAULT_ORG_POLICY.lateAfterMinutes,
  );
  let criticalLateAfterMinutes = clampMinutes(
    raw.criticalLateAfterMinutes,
    DEFAULT_ORG_POLICY.criticalLateAfterMinutes,
  );

  // A critical medication must never be given MORE slack than an ordinary one.
  // Getting this backwards is silent and inverts the whole point of marking a
  // medication critical, so it is corrected here rather than trusted.
  if (criticalLateAfterMinutes > lateAfterMinutes) {
    criticalLateAfterMinutes = lateAfterMinutes;
  }

  return {
    lateAfterMinutes,
    criticalLateAfterMinutes,
    staffCanSeeHistory: bool(raw.staffCanSeeHistory, DEFAULT_ORG_POLICY.staffCanSeeHistory),
    allowFamilyInvites: bool(raw.allowFamilyInvites, DEFAULT_ORG_POLICY.allowFamilyInvites),
  };
}

/** The escalation half of a policy, in the shape the engine takes. */
export function escalationFromPolicy(policy: OrgPolicy): EscalationPolicy {
  return {
    lateAfterMinutes: policy.lateAfterMinutes,
    criticalLateAfterMinutes: policy.criticalLateAfterMinutes,
    notifyOnLate: DEFAULT_ESCALATION_POLICY.notifyOnLate,
    notifyOnMissed: DEFAULT_ESCALATION_POLICY.notifyOnMissed,
  };
}

/**
 * Only the fields an admin may write, validated the same way as reading.
 *
 * Returns the blob to store. Deliberately re-serialises rather than merging
 * the caller's object, so an admin cannot smuggle an unknown key into the
 * settings column and have some future release start honouring it.
 */
export function sanitiseOrgPolicy(input: unknown): OrgPolicy {
  return parseOrgPolicy(input);
}
